import type { Localized } from '@/lib/api';

/**
 * locale.ts — resolving bilingual content against a language, and which way it runs.
 *
 * ### The distinction this file exists to keep
 *
 * **Administration language is not display language, and conflating them would
 * be a psychometric defect rather than a cosmetic one.**
 *
 * `administeredLocale` is pinned onto an instance at generation and stamped on
 * every result envelope, because *language of administration can moderate norms*
 * (contract v0 §A6) — it is reproducibility metadata, not a preference. An
 * operator's own console language, when Workstream H builds one, is an account
 * property they can change at will. A run's administration language cannot
 * change once a candidate has started reading it, and nothing in this file lets
 * anyone try.
 *
 * So the functions below take a locale as an argument. There is deliberately no
 * ambient "current language" to reach for: the harness resolves against the
 * *instance's* pinned locale, and a global would be the exact mechanism by which
 * an operator's preference silently rewrote what a candidate was administered.
 */

/** `ar-AE` → `ar`. Format is BCP-47-like and validated server-side. */
export function languageOf(locale: string): 'ar' | 'en' {
  return locale.slice(0, 2).toLowerCase() === 'ar' ? 'ar' : 'en';
}

/**
 * Text direction for a locale.
 *
 * Arabic is the only RTL language this platform serves today, so this is a
 * one-case check rather than a table — and a table of one entry would imply a
 * completeness it does not have. When a second RTL language appears it becomes a
 * set, and it will be obvious here rather than scattered across screens.
 */
export function directionOf(locale: string): 'rtl' | 'ltr' {
  return languageOf(locale) === 'ar' ? 'rtl' : 'ltr';
}

/**
 * The bilingual field in the requested language — **falling back to the other
 * rather than rendering blank.**
 *
 * A half-translated catalogue is the normal case, not the exception: items are
 * authored in one language and translated later, and certification requires both
 * only at the point of certification. So an Arabic administration of a module
 * with one untranslated item must show that item in English, not show an empty
 * card. A candidate can answer a question in the wrong language; they cannot
 * answer a blank one.
 *
 * Whitespace counts as absent — `{ ar: '  ' }` is an untranslated field that
 * happens to have survived a spreadsheet.
 */
export function t(text: Localized, locale: string): string {
  const language = languageOf(locale);
  const wanted = text[language];
  if (wanted?.trim()) return wanted;

  const other = language === 'ar' ? text.en : text.ar;
  return other?.trim() ? other : '';
}

/**
 * The *other* language, for the secondary line a QA harness shows.
 *
 * Empty when there is nothing to show — either the field is untranslated, or
 * `t()` already fell back to it and repeating it would render the same string
 * twice with different styling, which reads as a rendering bug.
 */
export function secondary(text: Localized, locale: string): string {
  const other = languageOf(locale) === 'ar' ? text.en : text.ar;
  if (!other?.trim()) return '';
  return other.trim() === t(text, locale).trim() ? '' : other;
}

/** How a locale should be named to an operator. */
export const LOCALE_LABEL: Record<string, string> = {
  'ar-AE': 'Arabic',
  'en-AE': 'English',
  'en-GB': 'English',
};

export const localeLabel = (locale: string): string =>
  LOCALE_LABEL[locale] ?? (languageOf(locale) === 'ar' ? 'Arabic' : 'English');
